import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FiArrowRight, FiCheckCircle, FiChevronRight, FiEdit2, FiSearch } from "react-icons/fi";

import { api, getOLTEvents, getOLTPorts, getOLTResources, getOverview, graphTargets, listONTs, OVERVIEW_REFRESH_MS } from "@/lib/api";
import { useSignalBands } from "@/lib/bands";
import { fmtDbm, classifyRx, signalColor, type SignalBands } from "@/lib/signal";
import { formatBps } from "@/lib/format";
import { fmtNum, useI18n, type Key } from "@/lib/i18n";
import { incidentTitle } from "@/lib/incident";
import {
  abnormalONTs, oltState, ponPortFor, ponSummaries, rxBuckets, type OntProblem, type PonSummary, type PonTier,
} from "@/lib/olthealth";
import { cn } from "@/lib/utils";
import type { OLT, OLTDetailResponse, OLTEvent, ONT } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ageOf, OltStateBadge, OnlineBar, TierMark, uptimeOf } from "@/components/OltStatus";
import { DeviceHealth, DeviceInfo, healthRows, Sensors } from "@/components/OLTHealth";
import OLTTraffic from "@/components/OLTTraffic";
import { OLTDialog } from "@/routes/OLTsPage";

const TABS = ["overview", "pons", "onts", "traffic", "health", "events"] as const;

/** A PON's traffic from the OLT's side: TX towards the ONTs, RX from them. */
type PonTraffic = { tx: number | null; rx: number | null; source: "CLI" | "LibreNMS" };
type TrafficFor = (path: string) => PonTraffic | undefined;
type Tab = (typeof TABS)[number];

function Panel({ title, right, children, className }: { title: string; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("min-w-0 rounded-lg border border-rule bg-panel", className)}>
      <header className="flex items-center justify-between gap-3 border-b border-rule px-4 py-2.5">
        <h3 className="eyebrow">{title}</h3>
        {right}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

function MoreLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal">
      {children} <FiArrowRight />
    </Link>
  );
}

function Calm({ text }: { text: string }) {
  return <p className="flex items-center gap-2 py-1 text-[13px] text-signal-good"><FiCheckCircle /> {text}</p>;
}

const PON_TIER_TEXT: Record<PonTier, string> = {
  down: "text-signal-crit", degraded: "text-signal-warn", warn: "text-signal-warn", healthy: "text-signal-good",
};
const PON_TIER_RANK: Record<PonTier, number> = { down: 0, degraded: 1, warn: 2, healthy: 3 };

/** "12h 17m": how long an ONT has been down; last_down_at when known, else last seen. */
const downFor = (o: Pick<ONT, "last_down_at" | "last_seen_at">, now: number) => ageOf(o.last_down_at ?? o.last_seen_at, now);

function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// ---------------------------------------------------------------------------

export default function OLTDetailPage() {
  const { t, locale } = useI18n();
  const { id = "" } = useParams<{ id: string }>();
  const [sp, setSp] = useSearchParams();
  const tab = (TABS as readonly string[]).includes(sp.get("tab") ?? "") ? (sp.get("tab") as Tab) : "overview";
  const pon = sp.get("pon");
  const now = useNow();
  const bands = useSignalBands();
  const [edit, setEdit] = useState(false);

  const go = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setSp(p);
  };

  const detail = useQuery({ queryKey: ["olt", id], queryFn: () => api<OLTDetailResponse>(`/api/v1/olts/${id}`), refetchInterval: 30_000, enabled: !!id });
  const ontsQ = useQuery({ queryKey: ["olt-onts", id], queryFn: () => listONTs({ oltId: id, limit: 5000 }), refetchInterval: 30_000, enabled: !!id });
  const res = useQuery({ queryKey: ["olt-resources", id], queryFn: () => getOLTResources(id), refetchInterval: 60_000, enabled: !!id });
  const ports = useQuery({ queryKey: ["olt-ports", id], queryFn: () => getOLTPorts(id), refetchInterval: 60_000, enabled: !!id });
  const ov = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: OVERVIEW_REFRESH_MS });
  const mgmtIP = detail.data?.olt.mgmt_ip ?? "";
  // Same cache key as the Traffic tab: one LibreNMS lookup serves both.
  const targets = useQuery({ queryKey: ["graph-targets", mgmtIP], queryFn: () => graphTargets(mgmtIP), enabled: !!mgmtIP, staleTime: 60_000 });

  const olt = detail.data?.olt as OLT | undefined;
  const onts = useMemo(() => ontsQ.data?.onts ?? [], [ontsQ.data]);
  const pollS = ov.data?.olt_poll_interval_s ?? 120;
  const pons = useMemo(() => ponSummaries(onts, bands, detail.data?.ports.map((p) => p.olt_index) ?? []), [onts, bands, detail.data]);
  const abnormal = useMemo(() => abnormalONTs(onts, bands), [onts, bands]);
  const cliPorts = ports.data?.ports ?? [];
  const libre = targets.data?.devices.find((d) => [d.hostname, d.name].some((v) => v.trim().toLowerCase() === mgmtIP.trim().toLowerCase()));
  // The OLT's own counter first; the LibreNMS interface when the CLI has none.
  const trafficFor: TrafficFor = (path) => {
    const c = ponPortFor(cliPorts, path);
    if (c) return { tx: c.tx_bps, rx: c.rx_bps, source: "CLI" };
    const l = ponPortFor((libre?.ports ?? []).map((p) => ({ ...p, name: p.ifname })), path);
    return l ? { tx: l.tx_bps, rx: l.rx_bps, source: "LibreNMS" } : undefined;
  };
  const live = ov.data?.olts.find((o) => String(o.id) === id);
  const online = onts.filter((o) => o.run_state === "Online").length;

  if (detail.isLoading) return <p className="text-slate-400">{t("common.loading")}</p>;
  if (!olt) return <p className="text-signal-crit">{(detail.error as Error | null)?.message ?? t("common.error")}</p>;

  const { state, reasons } = oltState(
    { status: olt.status, last_polled_at: olt.last_polled_at, onts_total: onts.length, onts_online: online, temp_c: res.data?.temp_c ?? null },
    pons.map((p) => ({ onts_total: p.total, onts_online: p.online })), pollS, now,
  );
  const polledStale = olt.last_polled_at ? (now - Date.parse(olt.last_polled_at)) / 1000 > pollS * 3 : true;
  const warnings = live ? live.alerts_warn + live.alerts_critical : null;
  const ponOpen = pon ? pons.find((p) => p.path === pon) : undefined;
  const tabCount: Partial<Record<Tab, number>> = {
    pons: pons.filter((p) => p.tier !== "healthy").length,
    onts: abnormal.length,
  };
  const href = (q: Record<string, string>) => `/olts/${id}?${new URLSearchParams(q)}`;

  return (
    <div className="mx-auto max-w-[1600px] space-y-4">
      {/* Breadcrumb: the operator always knows where they are in FLEET > OLT > PON. */}
      <nav className="flex items-center gap-1.5 text-[12px] text-slate-500">
        <Link to="/olts" className="hover:text-signal">{t("olt.fleet.title")}</Link>
        <FiChevronRight className="size-3" />
        {ponOpen ? <button type="button" onClick={() => go({ pon: null })} className="hover:text-signal">{olt.name}</button> : <span className="text-slate-300">{olt.name}</span>}
        {ponOpen && (<><FiChevronRight className="size-3" /><span className="data text-slate-300">PON {ponOpen.path}</span></>)}
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold tracking-tight text-slate-100">{olt.name}</h1>
            <OltStateBadge state={state} big />
          </div>
          {reasons.length > 0 && state !== "healthy" && (
            <p className="mt-1.5 text-[12.5px] text-slate-300">{reasons.map((r) => t(`olt.reason.${r}` as Key)).join(" · ")}</p>
          )}
          <p className="data mt-1.5 text-[12px] text-slate-400">
            {[olt.location, [olt.vendor, olt.model].filter(Boolean).join(" · "), olt.mgmt_ip].filter(Boolean).join("   ·   ")}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="flex items-center gap-2 text-[12px]">
            {olt.status === "unreachable" ? (
              <span className="rounded-sm bg-signal-crit/15 px-1.5 py-0.5 font-semibold tracking-wider text-signal-crit">{t("olt.pollFailed")}</span>
            ) : polledStale ? (
              <span className="rounded-sm bg-amber-400/10 px-1.5 py-0.5 font-semibold tracking-wider text-amber-200">{t("olt.staleTag")}</span>
            ) : (
              <><span className="beacon size-1.5 rounded-full bg-signal-good text-signal-good" /><span className="eyebrow text-signal-good">{t("live.live")}</span></>
            )}
            <span className="data text-slate-400">{olt.last_polled_at ? t("olt.polledAgo", { age: ageOf(olt.last_polled_at, now) }) : t("olt.never")}</span>
            {/* Uptime from an OLT that is not answering is an old reading, not a current fact. */}
            {olt.status !== "unreachable" && res.data?.uptime_s !== null && res.data?.uptime_s !== undefined && (
              <span className="data text-slate-500">· {t("olt.uptime", { up: uptimeOf(res.data.uptime_s) })}</span>
            )}
          </span>
          <Button size="xs" variant="ghost" onClick={() => setEdit(true)}><FiEdit2 /> {t("common.edit")}</Button>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-5">
        {[
          { k: "olt.kpi.pon", v: fmtNum(locale, pons.length), tone: "" },
          { k: "olt.kpi.ont", v: fmtNum(locale, onts.length), tone: "" },
          { k: "olt.kpi.online", v: fmtNum(locale, online), tone: online < onts.length ? "" : "text-signal-good" },
          { k: "olt.kpi.offline", v: fmtNum(locale, onts.length - online), tone: onts.length - online ? "text-signal-crit" : "text-slate-500" },
          { k: "olt.kpi.warnings", v: warnings === null ? "—" : fmtNum(locale, warnings), tone: warnings ? "text-signal-warn" : "text-slate-500" },
        ].map((f) => (
          <div key={f.k} className="bg-panel px-4 py-3">
            <div className="eyebrow">{t(f.k as Key)}</div>
            <div className={cn("data mt-1 text-[1.7rem] font-medium leading-none", f.tone || "text-slate-100")}>{ontsQ.isLoading ? "…" : f.v}</div>
          </div>
        ))}
      </section>

      {ponOpen ? (
        <PonDetail olt={olt} pon={ponOpen} onts={onts.filter((o) => o.pon_path === ponOpen.path)} bands={bands} now={now}
          traffic={trafficFor(ponOpen.path)} onBack={() => go({ pon: null })} href={href} />
      ) : (
        <>
          <nav className="flex gap-1 overflow-x-auto border-b border-rule" role="tablist">
            {TABS.map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => go({ tab: k === "overview" ? null : k, st: null })}
                className={cn("-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-[13px] transition-colors",
                  tab === k ? "border-signal text-slate-100" : "border-transparent text-slate-400 hover:text-slate-200")}>
                {t(`olt.tab.${k}` as Key)}
                {tabCount[k] ? <span className="data rounded-sm bg-signal-warn/15 px-1 text-[10.5px] text-signal-warn">{tabCount[k]}</span> : null}
              </button>
            ))}
          </nav>

          {tab === "overview" && (
            <div className="grid gap-4 lg:grid-cols-2">
              <ServiceImpact down={state === "down"} total={onts.length} pons={pons} abnormal={abnormal} now={now} href={href} go={go} />
              <Panel title={t("olt.h.title")} right={<MoreLink to={href({ tab: "health" })}>{t("olt.tab.health")}</MoreLink>}>
                <DeviceHealth compact rows={healthRows(olt, res.data, pollS, now, t)} />
              </Panel>
              <PonHealth pons={pons} go={go} href={href} />
              <OpticalHealth onts={onts} pons={pons} bands={bands} href={href} />
              {/* A dead OLT takes every ONT with it: one line, not a row per customer. */}
              {state === "down" ? (
                <Panel title={t("olt.ab.title")} className="lg:col-span-2"><p className="text-[13px] text-slate-400">{t("olt.ab.oltDown", { n: fmtNum(locale, onts.length) })}</p></Panel>
              ) : (
                <AbnormalTable rows={abnormal} total={onts.length} now={now} href={href} go={go} className="lg:col-span-2" />
              )}
            </div>
          )}
          {tab === "pons" && <PonTable pons={pons} trafficFor={trafficFor} go={go} />}
          {tab === "onts" && <OntInventory onts={onts} pons={pons} bands={bands} now={now} filter={sp.get("st")} ponFilter={sp.get("p")} go={go} />}
          {tab === "traffic" && <OLTTraffic olt={olt} cliPorts={cliPorts} nowMs={now} />}
          {tab === "health" && (
            <div className="grid gap-4 lg:grid-cols-2">
              <Panel title={t("olt.h.title")}><DeviceHealth rows={healthRows(olt, res.data, pollS, now, t)} /></Panel>
              <Panel title={t("olt.h.sensors")}><Sensors res={res.data} /></Panel>
              <Panel title={t("olt.info.title")} className="lg:col-span-2"><DeviceInfo olt={olt} warning={res.data?.cli?.warning} /></Panel>
            </div>
          )}
          {tab === "events" && <Events oltId={id} now={now} />}
        </>
      )}
      {edit && <OLTDialog edit={olt} onClose={() => setEdit(false)} />}
    </div>
  );
}

// --- Overview pieces ---------------------------------------------------------

type Go = (next: Record<string, string | null>) => void;
type Href = (q: Record<string, string>) => string;
type Abn = ONT & { problem: OntProblem };

function ServiceImpact({ down, total, pons, abnormal, now, href, go }: {
  down: boolean; total: number; pons: PonSummary[]; abnormal: Abn[]; now: number; href: Href; go: Go;
}) {
  const { t, locale } = useI18n();
  const dark = pons.filter((p) => p.tier === "down");
  const darkPaths = new Set(dark.map((p) => p.path));
  const allOffline = abnormal.filter((o) => o.problem.kind === "offline" || o.problem.kind === "missing");
  // A dark PON is one incident: its ONTs are not repeated as rows, so the one
  // customer offline somewhere else stays visible.
  const offline = allOffline.filter((o) => !darkPaths.has(o.pon_path ?? ""));
  return (
    <Panel title={t("olt.si.title")} right={allOffline.length > 0 && <MoreLink to={href({ tab: "onts", st: "offline" })}>{t("olt.si.all")}</MoreLink>}>
      {down ? (
        <div className="rounded-md border border-signal-crit/50 bg-signal-crit/10 px-3 py-2.5">
          <p className="font-semibold text-signal-crit">{t("olt.si.oltDown")}</p>
          <p className="mt-0.5 text-[12.5px] text-slate-300">{t("olt.si.potentially", { n: fmtNum(locale, total) })}</p>
        </div>
      ) : allOffline.length === 0 ? (
        <Calm text={t("olt.si.none")} />
      ) : (
        <div className="space-y-3">
          {dark.map((p) => (
            <button key={p.path} type="button" onClick={() => go({ pon: p.path })}
              className="flex w-full items-center gap-3 rounded-md border border-signal-crit/50 bg-signal-crit/10 px-3 py-2 text-left hover:border-signal-crit">
              <TierMark tier="down" />
              <span className="flex-1 font-semibold text-signal-crit">{t("olt.si.ponDown", { pon: p.path })}</span>
              <span className="data text-[12.5px] text-slate-300">0/{p.total}</span>
            </button>
          ))}
          {offline.length > 0 && (
            <p className="text-[13px] font-medium text-signal-crit">
              {t(dark.length ? "olt.si.offlineElsewhere" : "olt.si.offline", { n: fmtNum(locale, offline.length) })}
            </p>
          )}
          <ul className="divide-y divide-rule/60">
            {offline.slice(0, 5).map((o) => (
              <li key={o.id} className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 py-1.5">
                <TierMark tier="crit" />
                <Link to={`/onts/${o.id}`} className="truncate text-[13px] text-slate-100 hover:text-signal">{o.description || o.serial}</Link>
                <button type="button" onClick={() => go({ pon: o.pon_path })} className="data text-[12px] text-slate-400 hover:text-signal">{o.pon_path}</button>
                <span className="data text-[12.5px] text-slate-300">
                  {o.last_down_cause?.toLowerCase().includes("gasp") && <span className="mr-2 text-amber-200">{t("olt.powerLoss")}</span>}
                  {downFor(o, now)}
                </span>
              </li>
            ))}
          </ul>
          {offline.length > 5 && <MoreLink to={href({ tab: "onts", st: "offline" })}>{t("olt.moreN", { n: offline.length - 5 })}</MoreLink>}
        </div>
      )}
    </Panel>
  );
}

function PonHealth({ pons, go, href }: { pons: PonSummary[]; go: Go; href: Href }) {
  const { t, locale } = useI18n();
  // Abnormal PONs float; with many PONs the healthy ones collapse into a count.
  const ranked = [...pons].sort((a, b) => PON_TIER_RANK[a.tier] - PON_TIER_RANK[b.tier] || a.path.localeCompare(b.path, undefined, { numeric: true }));
  const cap = 8;
  const shown = ranked.slice(0, cap);
  const hiddenHealthy = ranked.slice(cap).filter((p) => p.tier === "healthy").length;
  const hiddenOther = ranked.length - cap - hiddenHealthy;
  return (
    <Panel title={t("olt.pon.title")} right={<MoreLink to={href({ tab: "pons" })}>{t("olt.tab.pons")}</MoreLink>}>
      {pons.length === 0 ? <p className="text-[12.5px] text-slate-500">{t("oltDetail.noPorts")}</p> : (
        <ul className="divide-y divide-rule/60">
          {shown.map((p) => (
            <li key={p.path}>
              <button type="button" onClick={() => go({ pon: p.path })} className="grid w-full grid-cols-[auto_4.5rem_1fr_auto] items-center gap-3 py-1.5 text-left hover:bg-white/[0.02]">
                <TierMark tier={p.tier} />
                <span className="data text-[13px] text-slate-100">{p.path}</span>
                <span className="flex items-center gap-3">
                  <span className="w-24"><OnlineBar online={p.online} total={p.total} /></span>
                  <span className="text-[12px]">
                    {p.offline > 0 && <span className="mr-2 text-signal-crit">{t("olt.offlineN", { n: p.offline })}</span>}
                    {p.weak > 0 && <span className="text-signal-warn">{t("olt.weakN", { n: p.weak })}</span>}
                  </span>
                </span>
                <span className="data text-[12.5px] text-slate-300">{fmtNum(locale, p.online)}/{fmtNum(locale, p.total)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {ranked.length > cap && (
        <p className="mt-2 text-[12px] text-slate-400">
          {hiddenOther === 0 ? t("olt.pon.otherHealthy", { n: hiddenHealthy }) : t("olt.pon.more", { n: ranked.length - cap })}
        </p>
      )}
    </Panel>
  );
}

function OpticalHealth({ onts, pons, bands, href }: { onts: ONT[]; pons: PonSummary[]; bands: SignalBands; href: Href }) {
  const { t, locale } = useI18n();
  const b = rxBuckets(onts, bands);
  const rx = onts.filter((o) => o.run_state === "Online" && o.rx_power_dbm !== null).map((o) => o.rx_power_dbm!);
  const avg = rx.length ? rx.reduce((a, c) => a + c, 0) / rx.length : null;
  const worst = rx.length ? Math.min(...rx) : null;
  const max = Math.max(1, b.good, b.moderate, b.weak, b.critical);
  const rows: { k: Key; n: number; bar: string; range: string }[] = [
    { k: "olt.opt.good", n: b.good, bar: "bg-signal-good/60", range: "> -20" },
    { k: "olt.opt.moderate", n: b.moderate, bar: "bg-slate-400/60", range: `-20 … ${bands.warn}` },
    { k: "olt.opt.weak", n: b.weak, bar: "bg-signal-warn", range: `${bands.warn} … ${bands.crit}` },
    { k: "olt.opt.critical", n: b.critical, bar: "bg-signal-crit", range: `< ${bands.crit}` },
  ];
  const weakPons = pons.filter((p) => p.weak > 0).sort((a, c) => c.weak - a.weak).slice(0, 3);
  return (
    <Panel title={t("olt.opt.title")} right={<MoreLink to={href({ tab: "onts", st: "weak" })}>{t("olt.opt.analysis")}</MoreLink>}>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.k} className="grid grid-cols-[6.5rem_6.5rem_1fr_2.5rem] items-center gap-3 text-[12.5px]">
            <span className="text-slate-300">{t(r.k)}</span>
            <span className="data text-[11px] text-slate-500">{r.range} dBm</span>
            <span className="h-1.5 overflow-hidden rounded-full bg-rule">
              <span className={cn("block h-full", r.bar)} style={{ width: `${r.n ? Math.max(3, (r.n / max) * 100) : 0}%` }} />
            </span>
            <span className={cn("data text-right", r.n ? "text-slate-100" : "text-slate-600")}>{fmtNum(locale, r.n)}</span>
          </div>
        ))}
        {b.noReading > 0 && <p className="text-[11.5px] text-slate-500">{t("olt.opt.noReading", { n: b.noReading })}</p>}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-rule pt-3">
        <div><dt className="eyebrow">{t("olt.opt.avg")}</dt><dd className="data mt-0.5 text-slate-100">{fmtDbm(avg)}</dd></div>
        <div><dt className="eyebrow">{t("olt.opt.worst")}</dt><dd className={cn("data mt-0.5", signalColor[classifyRx(worst, bands)])}>{fmtDbm(worst)}</dd></div>
      </dl>
      {weakPons.length > 0 && (
        <p className="mt-3 text-[12px] text-slate-400">
          {weakPons.map((p) => t("olt.opt.ponWeak", { pon: p.path, n: p.weak })).join("  ·  ")}
        </p>
      )}
    </Panel>
  );
}

function problemText(o: Abn, now: number, t: (k: Key, v?: Record<string, string | number>) => string) {
  switch (o.problem.kind) {
    case "offline": return { label: t("olt.p.offline"), value: downFor(o, now) };
    case "missing": return { label: t("olt.p.missing"), value: downFor(o, now) };
    case "rxCrit": return { label: t("olt.p.rxCrit"), value: fmtDbm(o.rx_power_dbm) };
    default: return { label: t("olt.p.rxWarn"), value: fmtDbm(o.rx_power_dbm) };
  }
}

function AbnormalTable({ rows, total, now, href, go, className, cap = 8 }: {
  rows: Abn[]; total: number; now: number; href: Href; go: Go; className?: string; cap?: number;
}) {
  const { t, locale } = useI18n();
  return (
    <Panel title={t("olt.ab.title")} className={className}
      right={<span className="text-[12px] text-slate-500">{t("olt.ab.count", { n: fmtNum(locale, rows.length), total: fmtNum(locale, total) })}</span>}>
      {rows.length === 0 ? <Calm text={t("olt.ab.none")} /> : (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left">
              {["", t("olt.ab.ont"), "PON", t("olt.ab.problem"), "", t("olt.ab.distance")].map((h, i) => (
                <th key={i} className={cn("eyebrow pb-2 font-medium", (i === 4 || i === 5) && "text-right")}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, cap).map((o) => {
              const p = problemText(o, now, t);
              return (
                <tr key={o.id} className="border-t border-rule/60">
                  <td className="w-5 py-1.5"><TierMark tier={o.problem.sev} /></td>
                  <td className="max-w-0 py-1.5 pr-3">
                    <Link to={`/onts/${o.id}`} className="block truncate text-slate-100 hover:text-signal">{o.description || o.serial}</Link>
                    <span className="data text-[11px] text-slate-500">{o.serial} · #{o.ont_id}</span>
                  </td>
                  <td className="py-1.5 pr-3"><button type="button" onClick={() => go({ pon: o.pon_path })} className="data text-[12.5px] text-slate-300 hover:text-signal">{o.pon_path}</button></td>
                  <td className={cn("py-1.5 pr-3 text-[11.5px] font-semibold uppercase tracking-wider", o.problem.sev === "crit" ? "text-signal-crit" : "text-signal-warn")}>
                    {p.label}
                    {o.problem.kind === "offline" && o.last_down_cause?.toLowerCase().includes("gasp") && (
                      <span className="ml-2 font-normal normal-case tracking-normal text-amber-200">{t("olt.powerLoss")}</span>
                    )}
                  </td>
                  <td className={cn("data py-1.5 text-right", o.problem.sev === "crit" ? "text-signal-crit" : "text-signal-warn")}>{p.value}</td>
                  <td className="data py-1.5 text-right text-[12px] text-slate-500">{o.distance_m === null ? "—" : o.distance_m >= 1000 ? `${(o.distance_m / 1000).toFixed(2)} km` : `${o.distance_m} m`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className="mt-3 flex gap-5">
        {rows.length > cap && <MoreLink to={href({ tab: "onts", st: "abnormal" })}>{t("olt.ab.allAbnormal", { n: rows.length })}</MoreLink>}
        <MoreLink to={href({ tab: "onts" })}>{t("olt.ab.allOnts", { n: fmtNum(locale, total) })}</MoreLink>
      </div>
    </Panel>
  );
}

// --- PONs --------------------------------------------------------------------

function PonTable({ pons, trafficFor, go }: { pons: PonSummary[]; trafficFor: TrafficFor; go: Go }) {
  const { t, locale } = useI18n();
  const [onlyBad, setOnlyBad] = useState(false);
  const shown = onlyBad ? pons.filter((p) => p.tier !== "healthy") : pons;
  const hasTraffic = pons.some((p) => trafficFor(p.path));
  return (
    <div className="space-y-3">
      {pons.length > 8 && (
        <label className="flex items-center gap-2 text-[12.5px] text-slate-300">
          <input type="checkbox" className="accent-cyan-400" checked={onlyBad} onChange={(e) => setOnlyBad(e.target.checked)} />
          {t("olt.pon.onlyAbnormal", { n: pons.filter((p) => p.tier !== "healthy").length })}
        </label>
      )}
      <div className="overflow-x-auto rounded-lg border border-rule bg-panel">
        <table className="w-full min-w-[760px] text-[13px]">
          <thead>
            <tr className="border-b border-rule text-left">
              {[t("olt.pon.col"), t("common.status"), "ONT", t("olt.kpi.offline"), t("olt.pon.weak"), t("olt.opt.avg"), t("olt.opt.worst"),
                ...(hasTraffic ? [t("olt.pon.traffic")] : [])].map((h, i) => (
                <th key={h} className={cn("eyebrow px-4 py-2.5 font-medium", i >= 2 && "text-right")}>{h}</th>
              ))}
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => {
              const port = trafficFor(p.path);
              return (
                <tr key={p.path} onClick={() => go({ pon: p.path })} className="group cursor-pointer border-b border-rule/60 last:border-b-0 hover:bg-white/[0.02]">
                  <td className="data px-4 py-2.5 text-slate-100 group-hover:text-signal">{p.path}</td>
                  <td className="px-4 py-2.5">
                    <span className={cn("inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-wider", PON_TIER_TEXT[p.tier])}>
                      <TierMark tier={p.tier} /> {t(`olt.ponTier.${p.tier}` as Key)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="data text-slate-100">{fmtNum(locale, p.online)}/{fmtNum(locale, p.total)}</div>
                    <div className="mt-1 ml-auto w-20"><OnlineBar online={p.online} total={p.total} /></div>
                  </td>
                  <td className={cn("data px-4 py-2.5 text-right", p.offline ? "text-signal-crit" : "text-slate-600")}>{p.offline || "—"}</td>
                  <td className={cn("data px-4 py-2.5 text-right", p.weak ? "text-signal-warn" : "text-slate-600")}>{p.weak || "—"}</td>
                  <td className="data px-4 py-2.5 text-right text-slate-300">{fmtDbm(p.avgRx)}</td>
                  <td className="data px-4 py-2.5 text-right text-slate-300">{fmtDbm(p.worstRx)}</td>
                  {hasTraffic && (
                    <td className="data px-4 py-2.5 text-right text-[12.5px] text-slate-300">
                      {port ? `↓${formatBps(port.tx)} ↑${formatBps(port.rx)}` : "—"}
                    </td>
                  )}
                  <td className="pr-3 text-slate-600 group-hover:text-signal"><FiChevronRight /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PonDetail({ olt, pon, onts, bands, now, traffic, onBack, href }: {
  olt: OLT; pon: PonSummary; onts: ONT[]; bands: SignalBands; now: number; traffic: PonTraffic | undefined; onBack: () => void; href: Href;
}) {
  const { t, locale } = useI18n();
  const abnormal = abnormalONTs(onts, bands);
  const sorted = [...onts].sort((a, b) => a.ont_id - b.ont_id);
  // Already on this PON: a PON label click here has nowhere to go.
  const go: Go = () => {};
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rule bg-panel px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="data text-lg text-slate-100">PON {pon.path}</h2>
          <span className={cn("inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider", PON_TIER_TEXT[pon.tier])}>
            <TierMark tier={pon.tier} /> {t(`olt.ponTier.${pon.tier}` as Key)}
          </span>
          <span className="text-[12.5px] text-slate-400">{olt.name}{olt.location ? ` · ${olt.location}` : ""}</span>
        </div>
        <Button size="xs" variant="secondary" onClick={onBack}>{t("olt.pon.back")}</Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title={t("olt.pon.onts")}>
          <div className="data text-[1.6rem] leading-none text-slate-100">{fmtNum(locale, pon.online)}<span className="text-base text-slate-500"> / {fmtNum(locale, pon.total)}</span></div>
          <div className="mt-2"><OnlineBar online={pon.online} total={pon.total} /></div>
          <p className="mt-2 text-[12.5px]">
            <span className={pon.offline ? "text-signal-crit" : "text-slate-500"}>{t("olt.offlineN", { n: pon.offline })}</span>
            <span className="text-slate-600"> · </span>
            <span className={pon.weak ? "text-signal-warn" : "text-slate-500"}>{t("olt.weakN", { n: pon.weak })}</span>
          </p>
        </Panel>
        <Panel title={t("olt.opt.title")}>
          <dl className="space-y-1.5 text-[13px]">
            <div className="flex justify-between"><dt className="text-slate-400">{t("olt.opt.avg")}</dt><dd className="data text-slate-100">{fmtDbm(pon.avgRx)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">{t("olt.opt.worst")}</dt><dd className={cn("data", signalColor[classifyRx(pon.worstRx, bands)])}>{fmtDbm(pon.worstRx)}</dd></div>
          </dl>
        </Panel>
        <Panel title={t("olt.pon.traffic")} right={traffic && <span className="data text-[10.5px] tracking-wider text-slate-600">{traffic.source}</span>}>
          {traffic ? (
            <dl className="space-y-1.5 text-[13px]">
              <div className="flex justify-between"><dt className="text-slate-400">↓ {t("olt.pon.toOnts")}</dt><dd className="data text-slate-100">{formatBps(traffic.tx)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">↑ {t("olt.pon.fromOnts")}</dt><dd className="data text-slate-100">{formatBps(traffic.rx)}</dd></div>
            </dl>
          ) : <p className="text-[12.5px] text-slate-500">{t("olt.pon.noTraffic")}</p>}
        </Panel>
      </div>

      <AbnormalTable rows={abnormal} total={onts.length} now={now} href={(q) => href({ ...q, p: pon.path })} go={go} cap={20} />

      <Panel title={t("olt.pon.allOnts", { n: onts.length })}>
        <OntRows rows={sorted} bands={bands} now={now} />
      </Panel>
    </div>
  );
}

// --- ONT inventory -----------------------------------------------------------

type StFilter = "all" | "abnormal" | "offline" | "weak" | "online";

function OntRows({ rows, bands, now }: { rows: ONT[]; bands: SignalBands; now: number }) {
  const { t } = useI18n();
  const [all, setAll] = useState(false);
  const cap = 200;
  const shown = all ? rows : rows.slice(0, cap);
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-[13px]">
          <thead>
            <tr className="text-left">
              {["", "#", t("olt.ab.ont"), t("olt.inv.serial"), "PON", t("common.status"), "RX", t("olt.ab.distance"), t("olt.inv.since")].map((h, i) => (
                <th key={i} className={cn("eyebrow pb-2 font-medium", i >= 6 && "text-right")}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((o) => {
              const on = o.run_state === "Online";
              const rx = classifyRx(o.rx_power_dbm, bands);
              return (
                <tr key={o.id} className="border-t border-rule/60">
                  <td className="w-5 py-1.5"><TierMark tier={!on ? "crit" : rx === "crit" ? "crit" : rx === "warn" ? "warn" : "healthy"} /></td>
                  <td className="data py-1.5 pr-3 text-slate-500">{o.ont_id}</td>
                  <td className="max-w-0 py-1.5 pr-3"><Link to={`/onts/${o.id}`} className="block truncate text-slate-100 hover:text-signal">{o.description || "—"}</Link></td>
                  <td className="data py-1.5 pr-3 text-[12px] text-slate-400">{o.serial}</td>
                  <td className="data py-1.5 pr-3 text-[12px] text-slate-400">{o.pon_path}</td>
                  <td className={cn("py-1.5 pr-3 text-[11.5px] font-semibold uppercase tracking-wider", on ? "text-signal-good/80" : "text-signal-crit")}>
                    {on ? t("status.online") : o.run_state === "Missing" ? t("olt.p.missing") : t("olt.p.offline")}
                  </td>
                  <td className={cn("data py-1.5 text-right", on ? signalColor[rx] : "text-slate-600")}>{on ? fmtDbm(o.rx_power_dbm) : "—"}</td>
                  <td className="data py-1.5 text-right text-[12px] text-slate-500">{o.distance_m === null ? "—" : o.distance_m >= 1000 ? `${(o.distance_m / 1000).toFixed(2)} km` : `${o.distance_m} m`}</td>
                  <td className="data py-1.5 text-right text-[12px] text-slate-500">{on ? "" : t("olt.downFor", { age: downFor(o, now) })}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!all && rows.length > cap && (
        <Button size="xs" variant="secondary" className="mt-3" onClick={() => setAll(true)}>{t("olt.inv.showAll", { n: rows.length })}</Button>
      )}
    </>
  );
}

function OntInventory({ onts, pons, bands, now, filter, ponFilter, go }: {
  onts: ONT[]; pons: PonSummary[]; bands: SignalBands; now: number; filter: string | null; ponFilter: string | null; go: Go;
}) {
  const { t, locale } = useI18n();
  const [q, setQ] = useState("");
  const st: StFilter = (["abnormal", "offline", "weak", "online"] as const).find((f) => f === filter) ?? "all";
  const isWeak = (o: ONT) => o.run_state === "Online" && (classifyRx(o.rx_power_dbm, bands) === "warn" || classifyRx(o.rx_power_dbm, bands) === "crit");
  const match: Record<StFilter, (o: ONT) => boolean> = {
    all: () => true,
    abnormal: (o) => o.run_state !== "Online" || isWeak(o),
    offline: (o) => o.run_state !== "Online",
    weak: isWeak,
    online: (o) => o.run_state === "Online",
  };
  const needle = q.trim().toLowerCase();
  const rows = onts.filter((o) => match[st](o) && (!ponFilter || o.pon_path === ponFilter) &&
    (!needle || [o.serial, o.description, o.pon_path, String(o.ont_id)].some((v) => v?.toLowerCase().includes(needle))));
  // Weak-RX view is an optical analysis: worst first. Everything else: PON, then ONT number.
  const sorted = st === "weak" ? [...rows].sort((a, b) => (a.rx_power_dbm ?? 0) - (b.rx_power_dbm ?? 0)) : rows;
  return (
    <section className="rounded-lg border border-rule bg-panel">
      <div className="flex flex-wrap items-center gap-2 border-b border-rule px-4 py-2.5">
        <div className="relative w-full max-w-64">
          <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("olt.inv.search")} className="h-8 border-rule bg-panel pl-8 text-[12.5px]" />
        </div>
        {(["all", "abnormal", "offline", "weak", "online"] as StFilter[]).map((f) => (
          <button key={f} type="button" onClick={() => go({ st: f === "all" ? null : f })}
            className={cn("rounded-sm border px-2 py-1 text-[12px]", st === f ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
            {t(`olt.inv.f.${f}` as Key)} <span className="data text-slate-500">{fmtNum(locale, onts.filter(match[f]).length)}</span>
          </button>
        ))}
        {pons.length > 1 && (
          <select value={ponFilter ?? ""} onChange={(e) => go({ p: e.target.value || null })}
            className="h-8 rounded-sm border border-rule bg-panel px-2 text-[12px] text-slate-300" aria-label="PON">
            <option value="">{t("olt.inv.allPons")}</option>
            {pons.map((p) => <option key={p.path} value={p.path}>PON {p.path}</option>)}
          </select>
        )}
      </div>
      <div className="px-4 py-3">
        {sorted.length === 0 ? <p className="py-4 text-center text-[12.5px] text-slate-500">{t("olt.inv.none")}</p> : <OntRows rows={sorted} bands={bands} now={now} />}
      </div>
    </section>
  );
}

// --- Events ------------------------------------------------------------------

type EvFilter = "all" | "critical" | "warning" | "ont" | "device";

function Events({ oltId, now }: { oltId: string; now: number }) {
  const { t, locale } = useI18n();
  const [days, setDays] = useState(7);
  const [f, setF] = useState<EvFilter>("all");
  const q = useQuery({ queryKey: ["olt-events", oltId, days], queryFn: () => getOLTEvents(oltId, days), refetchInterval: 60_000 });
  const crit = (e: OLTEvent) => e.severity === "critical" || e.event === "offline";
  const match: Record<EvFilter, (e: OLTEvent) => boolean> = {
    all: () => true,
    critical: crit,
    warning: (e) => e.severity === "warn",
    ont: (e) => e.kind === "ont" || e.target_type === "ont",
    device: (e) => e.target_type === "olt",
  };
  const items = (q.data?.items ?? []).filter(match[f]);
  const dayFmt = new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short" });
  const timeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });
  let lastDay = "";

  const title = (e: OLTEvent) => {
    if (e.kind === "alert") return incidentTitle(t, { metric: e.metric ?? "", message: e.message });
    const k = `olt.ev.${e.event}` as Key;
    return ["online", "offline", "down_cause", "alarm", "discovered"].includes(e.event ?? "") ? t(k) : e.event ?? "";
  };
  const tone = (e: OLTEvent) => (crit(e) ? "crit" : e.severity === "warn" ? "warn" : e.event === "online" ? "good" : "idle");

  return (
    <section className="rounded-lg border border-rule bg-panel">
      <div className="flex flex-wrap items-center gap-2 border-b border-rule px-4 py-2.5">
        {(["all", "critical", "warning", "ont", "device"] as EvFilter[]).map((k) => (
          <button key={k} type="button" onClick={() => setF(k)}
            className={cn("rounded-sm border px-2 py-1 text-[12px]", f === k ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
            {t(`olt.ev.f.${k}` as Key)} <span className="data text-slate-500">{(q.data?.items ?? []).filter(match[k]).length}</span>
          </button>
        ))}
        <span className="flex-1" />
        {[1, 7, 30].map((d) => (
          <Button key={d} size="xs" variant={d === days ? "default" : "secondary"} onClick={() => setDays(d)}>{d}d</Button>
        ))}
      </div>
      <div className="px-4 py-3">
        {q.isLoading && <p className="text-[12.5px] text-slate-500">{t("common.loading")}</p>}
        {!q.isLoading && items.length === 0 && <p className="py-4 text-center text-[12.5px] text-slate-500">{t("olt.ev.none")}</p>}
        <ol>
          {items.map((e, i) => {
            const day = dayFmt.format(new Date(e.at));
            const header = day !== lastDay;
            lastDay = day;
            const tn = tone(e);
            return (
              <li key={i}>
                {header && <div className="eyebrow mt-3 mb-1 first:mt-0">{day}</div>}
                <div className="grid grid-cols-[3.5rem_auto_1fr_auto] items-baseline gap-3 border-t border-rule/50 py-1.5">
                  <span className="data text-[12px] text-slate-500">{timeFmt.format(new Date(e.at))}</span>
                  <span className={cn("size-2 translate-y-[-1px] rounded-full",
                    tn === "crit" ? "bg-signal-crit" : tn === "warn" ? "bg-signal-warn" : tn === "good" ? "bg-signal-good" : "bg-slate-600")} />
                  <div className="min-w-0">
                    <span className="text-[13px] text-slate-100">{title(e)}</span>
                    {e.kind === "alert" && e.closed_at && <span className="ml-2 text-[11px] text-slate-500">{t("olt.ev.cleared")}</span>}
                    <div className="truncate text-[12px] text-slate-400">
                      {e.ont_id !== null && (
                        <Link to={`/onts/${e.ont_id}`} className="hover:text-signal">{e.description || e.serial || `#${e.ont_id}`}</Link>
                      )}
                      {e.pon_path && <span className="data"> · PON {e.pon_path}</span>}
                      {e.detail && <span> · {e.detail}</span>}
                      {e.value !== null && e.metric?.endsWith("_dbm") && <span className="data"> · {fmtDbm(e.value)}</span>}
                    </div>
                  </div>
                  <span className="data text-[11px] text-slate-600">{ageOf(e.at, now)}</span>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
